class XukTabs extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.clearSequence();
    this.abortController = new AbortController();
    this.tabs = Array.from(this.querySelectorAll('[data-xuk-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-xuk-panel]'));

    if (!this.tabs.length || !this.panels.length) return;

    const { signal } = this.abortController;

    this.tabs.forEach((tab) => {
      tab.addEventListener(
        'click',
        () => {
          const resumeLoop = this.dataset.xukSequenceLoop === 'true'
            && this.dataset.xukSequence !== 'smart-home'
            && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.clearSequence();
          this.activate(tab.dataset.xukTab);
          if (resumeLoop) this.startTabSequence();
        },
        { signal }
      );
      tab.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), { signal });
    }

    const requestedTab = this.getRequestedTab();
    const initialTab = requestedTab || this.dataset.initialTab || this.tabs[0].dataset.xukTab;
    this.dataset.xukTabsReady = 'true';
    this.activate(initialTab, false);
    if (this.dataset.xukSequence === 'smart-home') this.setupSmartHomePanelLayout(signal);
    this.setupSequence(signal);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.clearSequence();
  }

  getRequestedTab() {
    const queryKey = this.dataset.queryKey;
    if (!queryKey) return null;

    const value = new URLSearchParams(window.location.search).get(queryKey);
    return this.tabs.some((tab) => tab.dataset.xukTab === value) ? value : null;
  }

  activate(tabId, moveFocus = false) {
    const activeTab = this.tabs.find((tab) => tab.dataset.xukTab === tabId) || this.tabs[0];
    if (!activeTab) return;

    const activeId = activeTab.dataset.xukTab;

    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    const keepSmartHomePanelsInFlow = this.dataset.xukSequence === 'smart-home';

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.xukPanel === activeId;
      panel.hidden = keepSmartHomePanelsInFlow ? false : !isActive;
      panel.toggleAttribute('inert', keepSmartHomePanelsInFlow && !isActive);
      panel.setAttribute('aria-hidden', String(!isActive));
    });

    this.dataset.activeTab = activeId;
    if (window.matchMedia('(max-width: 989px)').matches) {
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      window.requestAnimationFrame(() => {
        const list = activeTab.closest('[role="tablist"]');
        if (!list || list.scrollWidth <= list.clientWidth) return;
        const listRect = list.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const offset = tabRect.left < listRect.left
          ? tabRect.left - listRect.left
          : Math.max(0, tabRect.right - listRect.right + 20);
        if (offset) list.scrollBy({ left: offset, behavior });
      });
    }
    if (this.dataset.xukSequence === 'smart-home') {
      this.syncSmartHomePanel();
    }
    if (moveFocus) activeTab.focus();
  }

  handleKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    if (currentIndex < 0) return;

    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    this.clearSequence();
    this.activate(this.tabs[nextIndex].dataset.xukTab, true);
    if (
      this.dataset.xukSequenceLoop === 'true'
      && this.dataset.xukSequence !== 'smart-home'
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      this.startTabSequence();
    }
  }

  handleBlockSelect(event) {
    const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
    const block = Array.from(this.querySelectorAll('[data-block-id]')).find(
      (item) => item.dataset.blockId === blockId
    );

    if (!block) return;

    this.clearSequence();
    const panel = block.closest('[data-xuk-panel]');
    if (panel) this.activate(panel.dataset.xukPanel, false);

    const details = block.matches('details') ? block : block.querySelector('details');
    if (details) details.open = true;
  }

  setupSequence(signal) {
    const sequence = this.dataset.xukSequence;
    if (!sequence) return;

    if (sequence === 'smart-home') {
      this.setupSmartHomeSequence(signal);
      return;
    }

    this.sequenceControl = this.querySelector('[data-xuk-sequence-control]');
    this.sequenceControl?.addEventListener('click', () => this.toggleTabSequence(), { signal });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || this.tabs.length < 2) {
      this.updateSequenceControl(false);
      return;
    }

    const threshold = Number(this.dataset.xukSequenceThreshold || 0.25);
    this.sequenceObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.sequenceObserver.disconnect();
        this.startTabSequence();
      },
      { threshold }
    );
    this.sequenceObserver.observe(this);
  }

  toggleTabSequence() {
    if (this.isSequencing) {
      this.clearSequence();
      return;
    }
    this.sequenceObserver?.disconnect();
    const allowLoop = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.startTabSequence(true, allowLoop);
  }

  startTabSequence(replay = false, allowLoop = true) {
    this.clearSequence(false);
    const shouldLoop = allowLoop && this.dataset.xukSequenceLoop === 'true';
    let index = this.tabs.findIndex((tab) => tab.dataset.xukTab === this.dataset.activeTab);
    if (replay || index < 0 || (!shouldLoop && index === this.tabs.length - 1)) {
      index = 0;
      this.activate(this.tabs[0].dataset.xukTab);
    }

    this.isSequencing = true;
    this.updateSequenceControl(true);
    const delay = Math.max(700, Number(this.dataset.xukSequenceDelay || 1400));

    const advance = () => {
      index += 1;
      if (index >= this.tabs.length) {
        if (!shouldLoop) {
          this.clearSequence();
          return;
        }
        index = 0;
      }
      this.activate(this.tabs[index].dataset.xukTab);
      this.sequenceTimer = window.setTimeout(advance, delay);
    };

    this.sequenceTimer = window.setTimeout(advance, delay);
  }

  updateSequenceControl(isPlaying) {
    if (!this.sequenceControl) return;
    const label = isPlaying
      ? this.sequenceControl.dataset.pauseLabel
      : this.sequenceControl.dataset.playLabel;
    if (!label) return;
    this.sequenceControl.textContent = label;
    this.sequenceControl.setAttribute('aria-label', label);
  }

  setupSmartHomeSequence(signal) {
    this.querySelectorAll('[data-xuk-state]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          this.clearSequence();
          this.activateSafetyState(button.closest('[data-xuk-panel]'), button.dataset.xukState);
        },
        { signal }
      );
    });

    this.querySelectorAll('[data-xuk-sequence-control]').forEach((control) => {
      control.addEventListener('click', () => this.toggleSafetySequence(control), { signal });
    });
    this.syncSmartHomePanel();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const threshold = Number(this.dataset.xukSequenceThreshold || 0.35);
    this.sequenceObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.sequenceObserver.disconnect();
        const panel = this.panels.find((item) => item.getAttribute('aria-hidden') === 'false');
        if (panel) this.startSafetySequence(panel);
      },
      { threshold }
    );
    this.sequenceObserver.observe(this);
  }

  setupSmartHomePanelLayout(signal) {
    const desktopQuery = window.matchMedia('(min-width: 990px)');
    desktopQuery.addEventListener?.(
      'change',
      () => this.activate(this.dataset.activeTab, false),
      { signal }
    );
  }

  toggleSafetySequence(control) {
    if (this.isSequencing) {
      this.clearSequence();
      return;
    }

    const panel = control.closest('[data-xuk-panel]');
    if (!panel) return;
    this.startSafetySequence(panel, true);
  }

  startSafetySequence(initialPanel, replay = false) {
    this.clearSequence(false);
    let panelIndex = Math.max(0, this.panels.indexOf(initialPanel));
    let panel = this.panels[panelIndex];
    let states = Array.from(panel.querySelectorAll('[data-xuk-state]'));
    if (!states.length) return;

    let stateIndex = replay
      ? 0
      : Math.max(0, states.findIndex((button) => button.getAttribute('aria-pressed') === 'true'));
    this.sequenceControl = panel.querySelector('[data-xuk-sequence-control]');
    this.isSequencing = true;
    this.updateSequenceControl(true);
    this.activateSafetyState(panel, states[stateIndex].dataset.xukState);
    const delay = Math.max(700, Number(this.dataset.xukSequenceDelay || 1050));

    const advance = () => {
      stateIndex += 1;
      if (stateIndex >= states.length) {
        this.updateSequenceControl(false);
        panelIndex = (panelIndex + 1) % this.panels.length;
        this.activate(this.tabs[panelIndex].dataset.xukTab);
        panel = this.panels[panelIndex];
        states = Array.from(panel.querySelectorAll('[data-xuk-state]'));
        stateIndex = 0;
        this.sequenceControl = panel.querySelector('[data-xuk-sequence-control]');
        this.updateSequenceControl(true);
      }

      this.activateSafetyState(panel, states[stateIndex].dataset.xukState);
      this.sequenceTimer = window.setTimeout(advance, delay);
    };

    this.sequenceTimer = window.setTimeout(advance, delay);
  }

  syncSmartHomePanel() {
    const panel = this.panels.find((item) => item.getAttribute('aria-hidden') === 'false');
    if (!panel) return;
    if (!this.isSequencing) this.sequenceControl = panel.querySelector('[data-xuk-sequence-control]');
    const activeButton = panel.querySelector('[data-xuk-state][aria-pressed="true"]');
    this.activateSafetyState(panel, activeButton?.dataset.xukState || 'normal');
  }

  activateSafetyState(panel, state) {
    if (!panel || !state) return;

    this.panels.forEach((item) => {
      if (item === panel) return;
      item.dataset.xukSafetyState = 'normal';
      item.querySelectorAll('[data-xuk-state]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.xukState === 'normal'));
      });
      const status = item.querySelector('[data-xuk-sequence-status]');
      const normalButton = item.querySelector('[data-xuk-state="normal"]');
      if (status && normalButton) status.textContent = normalButton.textContent.trim();
    });

    panel.dataset.xukSafetyState = state;
    panel.querySelectorAll('[data-xuk-state]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.xukState === state));
    });

    const activeButton = panel.querySelector(`[data-xuk-state="${state}"]`);
    const status = panel.querySelector('[data-xuk-sequence-status]');
    if (status && activeButton) status.textContent = activeButton.textContent.trim();

    const activeStep = { normal: -1, risk: 0, detected: 1, notified: 2, response: 3 }[state] ?? -1;
    this.closest('.xuk-smart-home')?.querySelectorAll('[data-xuk-flow-step]').forEach((step, index) => {
      step.classList.toggle('is-active', index === activeStep);
    });
  }

  clearSequence(updateControl = true) {
    if (this.sequenceTimer) window.clearTimeout(this.sequenceTimer);
    this.sequenceTimer = null;
    this.sequenceObserver?.disconnect();
    this.sequenceObserver = null;
    this.isSequencing = false;
    if (updateControl) this.updateSequenceControl(false);
  }
}

if (!customElements.get('xuk-tabs')) {
  customElements.define('xuk-tabs', XukTabs);
}
